import ProductClass from '../../../types/ProductClass';
import {
  getSessionStorageAndParse,
  safeRemoveSessionItem,
  stringifyAndSetSessionStorage,
} from '../../../util/storage';

const DEFAULT_MAX_QUANTITY = 99999;
const STRUCTURE_SELECTION_STORAGE_PREFIX = 'netstore.structureSelection.';

export const getStructureSelectionStorageKey = (productId) =>
  `${STRUCTURE_SELECTION_STORAGE_PREFIX}${String(productId || '')}`;

export const itemHasChildren = (item) =>
  Array.isArray(item?.children) && item.children.length > 0;

export const itemIsAvailable = (item) => item?.available !== false;

export const getStructureRowId = (item) =>
  String(item?.structure_item_id ?? item?.products_id ?? '');

export const getStructureItems = (product) =>
  product?.structure && Array.isArray(product.structure.products)
    ? product.structure.products
    : [];

export const getStructureRowRequestedQuantity = ({
  item,
  bundleQuantity = 1,
}) => {
  const rowQuantity = Number(item?.quantity);
  const normalizedRowQuantity =
    Number.isFinite(rowQuantity) && rowQuantity > 0 ? rowQuantity : 1;
  const normalizedBundleQuantity = Number(bundleQuantity);

  return (
    normalizedRowQuantity *
    (Number.isFinite(normalizedBundleQuantity) && normalizedBundleQuantity > 0
      ? normalizedBundleQuantity
      : 1)
  );
};

export const getAvailableChildren = (item) =>
  (Array.isArray(item?.children) ? item.children : []).filter(
    (child) => child.available !== false
  );

const productCanBeOrderedOutOfStock = (product) =>
  product?.canBeOrderedOutOfStock === true ||
  product?.can_be_ordered_out_of_stock === true;

export const getCartProductQuantities = (cartProducts = []) => {
  const quantities = {};
  const products = cartProducts
    ? Array.isArray(cartProducts)
      ? cartProducts
      : Array.from(cartProducts)
    : [];

  products.forEach((product) => {
    const productId = String(product?.product_id || product?.id || '');
    if (productId) {
      const quantity = Number(
        product?.quantity ?? product?.ordered_quantity ?? 0
      );
      if (Number.isFinite(quantity) && quantity > 0) {
        quantities[productId] = (quantities[productId] || 0) + quantity;
      }
    }

    // Structure/discount bundles only expose the main product in cart.products;
    // child demand is carried on bundle_products for stock checks.
    const bundleProducts = product?.bundle_products
      ? Array.isArray(product.bundle_products)
        ? product.bundle_products
        : Array.from(product.bundle_products)
      : [];

    bundleProducts.forEach((bundleProduct) => {
      const childId = String(bundleProduct?.product_id || '');
      if (!childId) {
        return;
      }

      const childQuantity = Number(bundleProduct?.quantity ?? 0);
      if (!Number.isFinite(childQuantity) || childQuantity <= 0) {
        return;
      }

      quantities[childId] = (quantities[childId] || 0) + childQuantity;
    });
  });

  return quantities;
};

export const getEffectiveFreeQuantity = (
  product,
  cartProductQuantities = {}
) => {
  const freeQuantity = Number(product?.free_quantity);
  if (!Number.isFinite(freeQuantity)) {
    return null;
  }

  if (productCanBeOrderedOutOfStock(product)) {
    // Align with getProductMaxQuantity / useMaxQuantity: OOS-orderable
    // products are not stock-limited (treat as unlimited).
    return null;
  }

  const productId = String(product?.products_id || product?.id || '');
  const inCart = Number(cartProductQuantities[productId] || 0);
  const reservedInCart = Number.isFinite(inCart) && inCart > 0 ? inCart : 0;

  return Math.max(0, freeQuantity - reservedInCart);
};

const productHasSufficientQuantity = (
  product,
  requestedQuantity,
  cartProductQuantities = {}
) => {
  const freeQuantity = getEffectiveFreeQuantity(product, cartProductQuantities);

  if (freeQuantity === null) {
    return true;
  }

  return requestedQuantity <= freeQuantity;
};

export const getStructureRowProduct = ({ item, mapping = {} }) => {
  if (!itemHasChildren(item)) {
    const rowId = getStructureRowId(item);

    if (Object.prototype.hasOwnProperty.call(mapping, rowId)) {
      return String(mapping[rowId]) === String(item.products_id) ? item : null;
    }

    return item;
  }

  const selectedProductId = mapping[getStructureRowId(item)];

  return getAvailableChildren(item).find(
    (child) => String(child.products_id) === String(selectedProductId)
  );
};

export const getStructureRowStockState = ({
  item,
  mapping = {},
  requestedQuantity,
  cartProductQuantities = {},
}) => {
  if (!itemIsAvailable(item)) {
    return null;
  }

  const selectedProduct = getStructureRowProduct({
    item,
    mapping,
  });

  if (!selectedProduct || selectedProduct.available === false) {
    return null;
  }

  const freeQuantity = getEffectiveFreeQuantity(
    selectedProduct,
    cartProductQuantities
  );

  if (freeQuantity === null || requestedQuantity <= freeQuantity) {
    return null;
  }

  return {
    freeQuantity,
    requestedQuantity,
    selectedProduct,
  };
};

const getBestAvailableChildByQuantity = (item, cartProductQuantities = {}) => {
  const availableChildren = getAvailableChildren(item);

  if (availableChildren.length === 0) {
    return null;
  }

  return availableChildren.reduce((bestChild, child) => {
    const childFreeQuantity = getEffectiveFreeQuantity(
      child,
      cartProductQuantities
    );
    const bestFreeQuantity = getEffectiveFreeQuantity(
      bestChild,
      cartProductQuantities
    );

    if (childFreeQuantity === null) {
      return child;
    }

    if (bestFreeQuantity === null || childFreeQuantity > bestFreeQuantity) {
      return child;
    }

    return bestChild;
  }, availableChildren[0]);
};

const getStructureRowBaseIssueState = ({
  item,
  mapping = {},
  requestedQuantity,
  cartProductQuantities = {},
}) => {
  if (!itemIsAvailable(item)) {
    return {
      type: 'unavailable',
    };
  }

  if (!itemHasChildren(item)) {
    const stockState = getStructureRowStockState({
      item,
      mapping,
      requestedQuantity,
      cartProductQuantities,
    });

    return stockState
      ? {
          type: 'stock',
          available: stockState.freeQuantity,
          requested: stockState.requestedQuantity,
          ...stockState,
        }
      : null;
  }

  const availableChildren = getAvailableChildren(item);
  const orderableChildren = availableChildren.filter((child) =>
    productHasSufficientQuantity(child, requestedQuantity, cartProductQuantities)
  );
  const selectedProduct = getStructureRowProduct({
    item,
    mapping,
  });

  if (selectedProduct) {
    const stockState = getStructureRowStockState({
      item,
      mapping,
      requestedQuantity,
      cartProductQuantities,
    });

    return stockState
      ? {
          type: 'stock',
          available: stockState.freeQuantity,
          requested: stockState.requestedQuantity,
          ...stockState,
        }
      : null;
  }

  if (availableChildren.length === 0) {
    return {
      type: 'unavailable',
    };
  }

  if (orderableChildren.length === 0) {
    const bestAvailableChild = getBestAvailableChildByQuantity(
      item,
      cartProductQuantities
    );
    const freeQuantity = getEffectiveFreeQuantity(
      bestAvailableChild,
      cartProductQuantities
    );

    return {
      type: 'stock',
      available: freeQuantity === null ? undefined : freeQuantity,
      requested: requestedQuantity,
      selectedProduct: bestAvailableChild,
    };
  }

  return null;
};

export const aggregateStructureChildDemand = ({
  structureItems,
  mapping = {},
  bundleQuantity = 1,
}) => {
  const aggregatedDemand = new Map();

  structureItems.forEach((item) => {
    const rowId = getStructureRowId(item);
    const selectedProduct = getStructureRowProduct({
      item,
      mapping,
    });

    if (!selectedProduct) {
      return;
    }

    const childProductId = String(selectedProduct.products_id);
    const rowRequestedQuantity = getStructureRowRequestedQuantity({
      item,
      bundleQuantity,
    });
    const perBundleDemand = getStructureRowRequestedQuantity({
      item,
      bundleQuantity: 1,
    });
    const existing = aggregatedDemand.get(childProductId) || {
      childProductId,
      selectedProduct,
      totalDemand: 0,
      perBundleDemand: 0,
      rowIds: [],
      rows: [],
    };

    existing.totalDemand += rowRequestedQuantity;
    existing.perBundleDemand += perBundleDemand;
    existing.rowIds.push(rowId);
    existing.rows.push({
      rowId,
      item,
      selectedProduct,
      requestedQuantity: rowRequestedQuantity,
      perBundleDemand,
    });

    aggregatedDemand.set(childProductId, existing);
  });

  return aggregatedDemand;
};

export const getStructureStockIssues = ({
  product,
  mapping = {},
  bundleQuantity = 1,
  cartProductQuantities = {},
}) => {
  const structureItems = getStructureItems(product);
  const issues = [];
  const issuesByRowId = {};

  structureItems.forEach((item) => {
    const rowId = getStructureRowId(item);
    const baseIssue = getStructureRowBaseIssueState({
      item,
      mapping,
      requestedQuantity: getStructureRowRequestedQuantity({
        item,
        bundleQuantity,
      }),
      cartProductQuantities,
    });

    if (baseIssue) {
      const issueWithItem = {
        rowId,
        item,
        ...baseIssue,
      };
      issuesByRowId[rowId] = issueWithItem;
      issues.push(issueWithItem);
    }
  });

  aggregateStructureChildDemand({
    structureItems,
    mapping,
    bundleQuantity,
  }).forEach((aggregatedEntry) => {
    const freeQuantity = getEffectiveFreeQuantity(
      aggregatedEntry.selectedProduct,
      cartProductQuantities
    );

    if (freeQuantity === null || aggregatedEntry.totalDemand <= freeQuantity) {
      return;
    }

    const maxBundles =
      aggregatedEntry.perBundleDemand > 0
        ? Math.floor(freeQuantity / aggregatedEntry.perBundleDemand)
        : DEFAULT_MAX_QUANTITY;
    const issue = {
      type: 'stock',
      childProductId: aggregatedEntry.childProductId,
      selectedProduct: aggregatedEntry.selectedProduct,
      available: freeQuantity,
      requested: aggregatedEntry.totalDemand,
      maxBundles,
      rowIds: aggregatedEntry.rowIds,
      rows: aggregatedEntry.rows,
    };

    aggregatedEntry.rowIds.forEach((rowId) => {
      issuesByRowId[rowId] = issue;
    });
    issues.push(issue);
  });

  return {
    issues,
    issuesByRowId,
  };
};

export const getStructureMaxBundles = ({
  product,
  mapping = {},
  cartProductQuantities = {},
}) => {
  const structureItems = getStructureItems(product);

  if (product?.class !== ProductClass.STRUCTURE || structureItems.length === 0) {
    return DEFAULT_MAX_QUANTITY;
  }

  const limits = [];

  aggregateStructureChildDemand({
    structureItems,
    mapping,
    bundleQuantity: 1,
  }).forEach((aggregatedEntry) => {
    const freeQuantity = getEffectiveFreeQuantity(
      aggregatedEntry.selectedProduct,
      cartProductQuantities
    );

    if (freeQuantity === null || aggregatedEntry.perBundleDemand <= 0) {
      return;
    }

    limits.push(Math.floor(freeQuantity / aggregatedEntry.perBundleDemand));
  });

  return limits.length > 0
    ? Math.max(0, Math.min(...limits))
    : DEFAULT_MAX_QUANTITY;
};

export const getStructureRowIssueState = ({
  product,
  item,
  mapping = {},
  requestedQuantity,
  cartProductQuantities = {},
}) => {
  const baseIssue = getStructureRowBaseIssueState({
    item,
    mapping,
    requestedQuantity,
    cartProductQuantities,
  });

  if (baseIssue?.type === 'unavailable') {
    return baseIssue;
  }

  const selectedProduct = getStructureRowProduct({
    item,
    mapping,
  });

  if (
    product &&
    selectedProduct &&
    selectedProduct.available !== false &&
    itemIsAvailable(item)
  ) {
    const perBundleQuantity = getStructureRowRequestedQuantity({
      item,
      bundleQuantity: 1,
    });
    const { issuesByRowId } = getStructureStockIssues({
      product,
      mapping,
      bundleQuantity:
        perBundleQuantity > 0 ? requestedQuantity / perBundleQuantity : 1,
      cartProductQuantities,
    });
    const aggregatedIssue = issuesByRowId[getStructureRowId(item)];

    if (aggregatedIssue?.type === 'stock') {
      return aggregatedIssue;
    }
  }

  if (baseIssue?.type === 'stock') {
    return baseIssue;
  }

  if (!selectedProduct && itemHasChildren(item)) {
    return getStructureRowBaseIssueState({
      item,
      mapping,
      requestedQuantity,
      cartProductQuantities,
    });
  }

  return null;
};

export const getPropertyDefinitions = (item) => {
  const defs = new Map();

  getAvailableChildren(item).forEach((child) => {
    (child.properties || []).forEach((prop) => {
      const existing = defs.get(prop.id) || {
        id: prop.id,
        name: prop.name,
        values: new Set(),
      };

      existing.values.add(prop.value);
      defs.set(prop.id, existing);
    });
  });

  return Array.from(defs.values()).map((definition) => ({
    id: definition.id,
    name: definition.name,
    values: Array.from(definition.values),
  }));
};

const childMatchesSelection = (child, selectionMap) => {
  const props = child.properties || [];

  return Object.entries(selectionMap).every(([propId, selectedValue]) => {
    if (!selectedValue) {
      return true;
    }

    return props.some(
      (prop) =>
        String(prop.id) === String(propId) && prop.value === selectedValue
    );
  });
};

export const selectionCanResolveToAvailableChild = ({ item, selectionMap }) =>
  getAvailableChildren(item).some((child) =>
    childMatchesSelection(child, selectionMap)
  );

export const propertyValueIsSelectable = ({
  item,
  selectionMap,
  propId,
  value,
}) =>
  selectionCanResolveToAvailableChild({
    item,
    selectionMap: {
      ...selectionMap,
      [propId]: value,
    },
  });

export const findChildByProperties = ({ item, selectionMap }) => {
  const propertyDefinitions = getPropertyDefinitions(item);

  if (
    propertyDefinitions.length === 0 ||
    !propertyDefinitions.every((propDef) => !!selectionMap[propDef.id])
  ) {
    return null;
  }

  const candidates = getAvailableChildren(item).filter((child) =>
    propertyDefinitions.every((propDef) => {
      const selectedValue = selectionMap[propDef.id];

      return (child.properties || []).some(
        (prop) =>
          String(prop.id) === String(propDef.id) && prop.value === selectedValue
      );
    })
  );

  return candidates.length === 1 ? candidates[0] : null;
};

export const structureRowIsValid = ({
  product,
  item,
  mapping = {},
  requestedQuantity,
  cartProductQuantities = {},
}) => {
  if (
    getStructureRowIssueState({
      product,
      item,
      mapping,
      requestedQuantity,
      cartProductQuantities,
    })
  ) {
    return false;
  }

  if (!itemHasChildren(item)) {
    return true;
  }

  return !!mapping[getStructureRowId(item)];
};

export const structureItemBlocksBundle = ({
  product,
  item,
  mapping = {},
  requestedQuantity,
  cartProductQuantities = {},
}) => {
  if (
    getStructureRowIssueState({
      product,
      item,
      mapping,
      requestedQuantity,
      cartProductQuantities,
    })
  ) {
    return true;
  }

  if (!itemHasChildren(item)) {
    return false;
  }

  return !mapping[getStructureRowId(item)];
};

const buildOrderedStructureItems = (structureItems, prioritizedRowId) => {
  if (!prioritizedRowId) {
    return structureItems;
  }

  return [
    ...structureItems.filter(
      (item) => getStructureRowId(item) === String(prioritizedRowId)
    ),
    ...structureItems.filter(
      (item) => getStructureRowId(item) !== String(prioritizedRowId)
    ),
  ];
};

export const buildStructureSelectionState = ({
  product,
  propertySelections = {},
  prioritizedRowId,
  bundleQuantity = 1,
  cartProductQuantities = {},
}) => {
  const structureItems = getStructureItems(product);

  if (
    product?.class !== ProductClass.STRUCTURE ||
    structureItems.length === 0
  ) {
    return {
      mapping: {},
      propertySelections: {},
      isValid: true,
    };
  }

  const mapping = {};
  const nextPropertySelections = {};

  structureItems.forEach((item) => {
    const rowId = getStructureRowId(item);

    if (!itemHasChildren(item)) {
      mapping[rowId] = itemIsAvailable(item)
        ? String(item.products_id)
        : undefined;
      return;
    }

    nextPropertySelections[rowId] = propertySelections[rowId] || {};
  });

  buildOrderedStructureItems(structureItems, prioritizedRowId).forEach(
    (item) => {
      if (!itemHasChildren(item)) {
        return;
      }

      const rowId = getStructureRowId(item);
      const selectedChild = findChildByProperties({
        item,
        selectionMap: nextPropertySelections[rowId] || {},
      });

      mapping[rowId] = selectedChild
        ? String(selectedChild.products_id)
        : undefined;
    }
  );

  return {
    mapping,
    propertySelections: nextPropertySelections,
    isValid: structureItems.every((item) => {
      return !structureItemBlocksBundle({
        product,
        item,
        mapping,
        requestedQuantity: getStructureRowRequestedQuantity({
          item,
          bundleQuantity,
        }),
        cartProductQuantities,
      });
    }),
  };
};

export const sanitizePersistedPropertySelections = (
  product,
  propertySelections = {}
) => {
  const structureItems = getStructureItems(product);
  if (
    !propertySelections ||
    typeof propertySelections !== 'object' ||
    structureItems.length === 0
  ) {
    return {};
  }

  const sanitized = {};

  structureItems.forEach((item) => {
    if (!itemHasChildren(item)) {
      return;
    }

    const rowId = getStructureRowId(item);
    const rowSelections = propertySelections[rowId];
    if (!rowSelections || typeof rowSelections !== 'object') {
      return;
    }

    const allowedPropIds = new Set(
      getPropertyDefinitions(item).map((propDef) => String(propDef.id))
    );
    const nextRowSelections = {};

    Object.keys(rowSelections).forEach((propId) => {
      const value = rowSelections[propId];
      if (
        !allowedPropIds.has(String(propId)) ||
        value == null ||
        value === ''
      ) {
        return;
      }

      if (
        propertyValueIsSelectable({
          item,
          selectionMap: rowSelections,
          propId,
          value,
        })
      ) {
        nextRowSelections[propId] = value;
      }
    });

    if (Object.keys(nextRowSelections).length > 0) {
      sanitized[rowId] = nextRowSelections;
    }
  });

  return sanitized;
};

export const getPersistedStructureSelection = (productId) => {
  if (!productId) {
    return null;
  }

  const data = getSessionStorageAndParse(
    getStructureSelectionStorageKey(productId)
  );
  if (!data || typeof data !== 'object') {
    return null;
  }

  const quantity = Number(data.quantity);
  return {
    propertySelections:
      data.propertySelections && typeof data.propertySelections === 'object'
        ? data.propertySelections
        : {},
    quantity:
      Number.isFinite(quantity) && quantity > 0 ? quantity : undefined,
  };
};

export const persistStructureSelection = (productId, patch = {}) => {
  if (!productId) {
    return;
  }

  const existing = getPersistedStructureSelection(productId) || {};
  const nextPropertySelections =
    patch.propertySelections !== undefined
      ? patch.propertySelections
      : existing.propertySelections || {};
  const nextQuantity =
    patch.quantity !== undefined ? patch.quantity : existing.quantity;

  stringifyAndSetSessionStorage(getStructureSelectionStorageKey(productId), {
    propertySelections: nextPropertySelections || {},
    ...(Number.isFinite(Number(nextQuantity)) && Number(nextQuantity) > 0
      ? { quantity: Number(nextQuantity) }
      : {}),
  });
};

export const clearPersistedStructureSelection = (productId) => {
  if (!productId) {
    return;
  }

  safeRemoveSessionItem(getStructureSelectionStorageKey(productId));
};

export const getInitialStructureSelectionState = (
  product,
  bundleQuantity = 1,
  cartProductQuantities = {},
  propertySelections
) => {
  const persisted =
    propertySelections === undefined
      ? getPersistedStructureSelection(product?.id)
      : null;
  const initialPropertySelections =
    propertySelections !== undefined
      ? propertySelections
      : persisted?.propertySelections || {};
  const initialBundleQuantity =
    Number.isFinite(Number(bundleQuantity)) && Number(bundleQuantity) > 0
      ? Number(bundleQuantity)
      : persisted?.quantity || 1;

  return buildStructureSelectionState({
    product,
    propertySelections: sanitizePersistedPropertySelections(
      product,
      initialPropertySelections
    ),
    bundleQuantity: initialBundleQuantity,
    cartProductQuantities,
  });
};
